import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ApiErrorStatusResponse } from '@workspace/data-types';
import { catchError, EMPTY, filter, Observable, tap } from 'rxjs';
import { AlertSnackBarService } from '../../../../services/alert-snackbar.service';
import { EnumsNiceLabelUtils } from '../../../../utils/enums-nice-label.utils';
import { codeFormatValidator } from '../../../../validators/code-format.validator';
import { AbstractDialogAuraComponent, DialogData } from '../../../abstract/dialog.abstract.component';
import { AuraInputCodeVerificationComponent } from '../../../inputs/code-verification/code-verification.component';
import { DialogLayoutAuraComponent } from '../../../layouts/dialog/dialog.component';

export interface CodeVerificationDialogData<TAction, TResult = unknown> extends DialogData {
  action: TAction;
  onComplete: (params: { action: TAction; token: string }) => Observable<TResult>;
  onResend: () => Observable<boolean>;
}

@Component({
  selector: 'aura-code-verification-dialog',
  templateUrl: './code-verification.component.html',
  imports: [DialogLayoutAuraComponent, MatButtonModule, MatIconModule, AuraInputCodeVerificationComponent],
})
export class CodeVerificationDialogAuraComponent<TAction> extends AbstractDialogAuraComponent<
  CodeVerificationDialogData<TAction>
> {
  private readonly destroyRef = inject(DestroyRef);
  private alertSnackbar = inject(AlertSnackBarService);

  codeControl = new FormControl('', [Validators.required, codeFormatValidator]);
  resendCount = signal<number>(0);

  onComplete(token: string): void {
    this.data
      .onComplete({ action: this.data.action, token })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((dto) => !!dto),
        tap((dto) => this.dialogRef.close(dto)),
        catchError((errorResponse) => {
          const apiError = errorResponse.error as ApiErrorStatusResponse;
          this.alertSnackbar.error({
            message: EnumsNiceLabelUtils.erroCode(apiError.statusCode, apiError.code),
            title: "Une erreur s'est produite",
          });
          return EMPTY;
        }),
      )
      .subscribe();
  }

  resend() {
    this.data
      .onResend()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.alertSnackbar.info({
            title: "Vérification de l'utilisateur",
            message: 'Un nouveau code vient de vous être envoyé.',
          });
        }),
      )
      .subscribe();
  }

  onResend() {
    setTimeout(() => {
      this.resendCount.update((v) => v + 1);
    }, 15000);
    this.resend();
  }
}
